/**
 * LeadKeyboardHelp - Keyboard shortcuts help overlay
 *
 * Shows available keyboard shortcuts for the leads list.
 * Press '?' to toggle visibility.
 */

import { cn } from '@limo/shared'
import { X, Keyboard } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { KEYBOARD_SHORTCUTS } from '@/hooks/useLeadKeyboardNavigation'

interface LeadKeyboardHelpProps {
  isOpen: boolean
  onClose: () => void
}

export function LeadKeyboardHelp({ isOpen, onClose }: LeadKeyboardHelpProps) {
  // Group shortcuts by category
  const navigationShortcuts = KEYBOARD_SHORTCUTS.filter(
    (s) => s.category === 'navigation'
  )
  const actionShortcuts = KEYBOARD_SHORTCUTS.filter(
    (s) => s.category === 'actions'
  )
  const stageShortcuts = KEYBOARD_SHORTCUTS.filter(
    (s) => s.category === 'stages'
  )

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Keyboard className="h-5 w-5" />
            Keyboard Shortcuts
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Navigation */}
          <div>
            <h3 className="text-sm font-semibold text-muted-foreground mb-3">
              Navigation
            </h3>
            <div className="space-y-2">
              {navigationShortcuts.map((shortcut) => (
                <ShortcutRow
                  key={shortcut.key}
                  label={shortcut.label}
                  description={shortcut.description}
                />
              ))}
            </div>
          </div>

          {/* Quick Actions */}
          <div>
            <h3 className="text-sm font-semibold text-muted-foreground mb-3">
              Quick Actions
            </h3>
            <div className="space-y-2">
              {actionShortcuts.map((shortcut) => (
                <ShortcutRow
                  key={shortcut.key}
                  label={shortcut.label}
                  description={shortcut.description}
                />
              ))}
            </div>
          </div>

          {/* Stage Transitions */}
          <div>
            <h3 className="text-sm font-semibold text-muted-foreground mb-3">
              Stage Transitions
            </h3>
            <div className="space-y-2">
              {stageShortcuts.map((shortcut) => (
                <ShortcutRow
                  key={shortcut.key}
                  label={shortcut.label}
                  description={shortcut.description}
                />
              ))}
            </div>
          </div>

          <p className="text-xs text-muted-foreground text-center pt-2 border-t">
            First select a lead with <kbd className="kbd">j</kbd>/<kbd className="kbd">k</kbd>, then use action shortcuts
          </p>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function ShortcutRow({
  label,
  description,
}: {
  label: string
  description: string
}) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm">{description}</span>
      <kbd className="kbd">{label}</kbd>
    </div>
  )
}

/**
 * LeadKeyboardHint - Small hint badge showing keyboard shortcut availability
 */
export function LeadKeyboardHint({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        'inline-flex items-center gap-1.5 text-xs text-muted-foreground',
        className
      )}
    >
      <Keyboard className="h-3.5 w-3.5" />
      <span>
        Press <kbd className="kbd">?</kbd> for shortcuts
      </span>
    </div>
  )
}
